import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { AdminIcon } from "@/components/admin/icon";
import {
	MARKETING_NAV,
	type MarketingNavItem,
} from "@/config/marketing-nav";
import { usePermissions } from "@/hooks/use-permissions";
import { cn } from "@/lib/cn";

/** L'utilisateur peut-il ouvrir cette entrée ? */
function autorise(
	item: MarketingNavItem,
	hasAny: (...c: string[]) => boolean,
	isSuperAdmin: boolean,
): boolean {
	return !item.permissions || isSuperAdmin || hasAny(...item.permissions);
}

/**
 * Coquille de « Marketing & Contenus » : colonne de sous-navigation groupée
 * à gauche, écran courant à droite.
 *
 * Les autres sections du back-office utilisent une barre d'onglets en
 * pastilles. Ici, dix-sept écrans à plat débordaient de la largeur et
 * perdaient toute lisibilité : une colonne les tient tous, groupés par
 * famille, et reste visible pendant qu'on fait défiler un long formulaire.
 *
 * Le contrôle d'accès vaut aussi par l'URL : une entrée masquée par les
 * permissions n'est pas atteignable en tapant son adresse — sans quoi la
 * barre latérale ne serait qu'une suggestion.
 */
export function MarketingLayout() {
	const { pathname } = useLocation();
	const { hasAny, isSuperAdmin } = usePermissions();

	const groupes = MARKETING_NAV.map((g) => ({
		...g,
		items: g.items.filter((i) => autorise(i, hasAny, isSuperAdmin)),
	})).filter((g) => g.items.length > 0);

	// Entrée correspondant à l'URL : préfixe le plus long d'abord, pour que
	// /marketing/bannieres ne soit pas capté par /marketing.
	const toutes = MARKETING_NAV.flatMap((g) => g.items);
	const courante = [...toutes]
		.sort((a, b) => b.to.length - a.to.length)
		.find((i) =>
			i.end
				? pathname === i.to
				: pathname === i.to ||
					pathname.startsWith(`${i.to}/`) ||
					(i.also ?? []).some((p) => pathname.startsWith(p)),
		);

	if (courante && !autorise(courante, hasAny, isSuperAdmin)) {
		const repli = groupes[0]?.items[0];
		return <AccesRefuse repli={repli} />;
	}

	return (
		<div className="cms-layout">
			<aside className="cms-subnav" data-testid="marketing-subnav">
				{groupes.map((g) => (
					<div key={g.group}>
						<div className="grp-h">{g.group}</div>
						{g.items.map((item) => (
							<NavLink
								key={item.to}
								to={item.to}
								end={item.end}
								className={({ isActive }) =>
									cn(
										"cms-navlink",
										(isActive ||
											(item.also ?? []).some((p) => pathname.startsWith(p))) &&
											"on",
									)
								}
								data-testid={`marketing-nav-${item.to.replaceAll("/", "-").replace(/^-/, "")}`}
							>
								<span className="ic">
									<AdminIcon n={item.icn} s={15} c="currentColor" />
								</span>
								<span>{item.label}</span>
							</NavLink>
						))}
					</div>
				))}
			</aside>
			<div style={{ minWidth: 0 }}>
				<Outlet />
			</div>
		</div>
	);
}

function AccesRefuse({ repli }: { repli?: MarketingNavItem }) {
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				gap: 12,
				padding: "72px 24px",
				textAlign: "center",
			}}
			data-testid="marketing-access-denied"
		>
			<span
				style={{
					display: "inline-flex",
					padding: 14,
					borderRadius: "50%",
					background: "var(--a-terra-soft)",
					color: "var(--a-terracotta)",
				}}
			>
				<AdminIcon n="shield" s={26} c="currentColor" />
			</span>
			<h2 style={{ fontSize: 17, fontWeight: 600, color: "var(--a-ink)" }}>
				Accès restreint
			</h2>
			<p style={{ fontSize: 13, color: "var(--a-ink-soft)", maxWidth: 380 }}>
				Votre compte ne dispose pas des permissions nécessaires pour consulter
				cet écran. Contactez un administrateur si vous pensez qu'il s'agit
				d'une erreur.
			</p>
			{repli && (
				<Link to={repli.to} className="btn" style={{ marginTop: 6 }}>
					Aller à « {repli.label} »
				</Link>
			)}
		</div>
	);
}
